import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RouterErrorBoundary, NotFoundPage } from '../components/ErrorPage'

// Auth screen imports
import { AuthLayout } from '../app/auth/_layout'
import { IdentificationMethodScreen } from '../app/auth/identification-method'
import { IdentifyWithEmailScreen } from '../app/auth/identifier/with-email'
import { IdentifyWithPhoneScreen } from '../app/auth/identifier/with-phone'
import { EnterPasswordScreen } from '../app/auth/enter-password'
import { SignUpCreatePasswordScreen } from '../app/auth/signup/create-password'
import { SignUpEnterVerificationCodeScreen } from '../app/auth/signup/enter-verification-code'

// Password recovery screen imports
import { RecoverPasswordWithEmailScreen } from '../app/auth/forgot-password/recover-with-email'
import { RecoverPasswordWithPhoneScreen } from '../app/auth/forgot-password/recover-with-phone'
import { ForgotPasswordVerificationCodeScreen } from '../app/auth/forgot-password/verification-code'
import { CreateNewPasswordScreen } from '../app/auth/forgot-password/create-new-password'

// Main screens
import { MainScreen } from '../app/main'
import { DashboardScreen } from '../app/dashboard'
import { SetupProfileScreen } from '../app/setup-profile'
import { DebugApiScreen } from '../app/debug-api'
import { ProfileSettingsScreen } from '../app/profile/profile-settings'
import { EditProfileScreen } from '../app/profile/edit-profile'
import { UpdatePhoneNumberScreen } from '../app/profile/update-phone-number'
import { VerifyPhoneNumberScreen } from '../app/profile/update-phone-number/verify'
import { UpdateProfileScreen } from '../app/profile/update-profile'
import { AddNewEmailScreen } from '../app/profile/update-profile/update-email/add-new-email'
import { ValidateEmailCodeScreen } from '../app/profile/update-profile/update-email/validate-code'
import { NewEmailValidateCodeScreen } from '../app/profile/update-profile/update-email/new-email-validate-code'
import { AddNewPhoneNumberScreen } from '../app/profile/update-profile/update-phone/add-new-phone-number'
import { ValidatePhoneCodeScreen } from '../app/profile/update-profile/update-phone/phone-number-validate-code'
import { NewPhoneValidateCodeScreen } from '../app/profile/update-profile/update-phone/new-phone-number-validate-code'
import { ChooseLanguageScreen } from '../app/choose-language'
import { ChoosePaymentCurrencyScreen } from '../app/choose-payment-currency'
import { SuccessfulProfileUpdateScreen } from '../app/successful-profile-update'
import { ActivityScreen } from '../app/activity'
import { RequestDetailsScreen } from '../app/request-details/[requestId]'
import { RequestsScreen } from '../app/requests'
import { PaymentLinkClaimScreen } from '../app/payment-link/[code]'
import { SendRequestScreen } from '../app/send-request'
import { SendRequestWithUserScreen } from '../app/send-request/with-user'
import { RecipientSelectionScreen } from '../app/send-request/recipient-selection'
import SendRequestRecipientScreen from '../app/send-request/recipient'
import { SendRequestConfirmScreen } from '../app/send-request/confirm'
import { TransactionSuccessScreen } from '../app/send-request/success'
import { MultipleRecipientsConfirmScreen } from '../app/send-request/confirm-multiple'
import { MultipleRequestsSuccessScreen } from '../app/send-request/success-multiple'
import { SplitRequestSelectUsersScreen } from '../app/split-request/select-users'
import { SplitRequestConfirmScreen } from '../app/split-request/confirm'
import { SplitRequestSuccessScreen } from '../app/split-request/success'
import { CommunityScreen } from '../app/community'
import { KemCardScreen } from '../app/kem-card'
import { KemCardChooseStyleScreen } from '../app/kem-card/choose-style'
import { KemCardInfoConfirmationScreen } from '../app/kem-card/info-confirmation'
import { KemCardDetailsScreen } from '../app/kem-card/details'
import { KemCardInfoScreen } from '../app/kem-card/card-info'
import { KemCardWalletInstructionsScreen } from '../app/kem-card/wallet-instructions'
import { KemCardCreationFailureScreen } from '../app/kem-card/creation-failure'
import { KemCardActivitiesScreen } from '../app/kem-card/activities'
import { UpdateLegalNameScreen } from '../app/kem-card/update-legal-name'
import { SelectDepositTypeScreen } from '../app/add-money/select-deposit-type'
import { SelectCurrencyScreen } from '../app/add-money/select-currency'
import AddMoneyScreen from '../app/add-money'
import ConfirmAddMoneyScreen from '../app/add-money/confirm'
import AddMoneySuccessScreen from '../app/add-money/success'
import ApplePayCheckoutScreen from '../app/apple-pay-checkout'
import AddPhoneScreen from '../app/add-phone'
import { CheckYourEmailScreen } from '../app/check-your-email'
import EnterPhoneScreen from '../app/add-phone/enter-phone'
import AddPhoneVerificationScreen from '../app/add-phone/verification'
import { CurrencyDepositScreen } from '../app/currency-deposit'
import { ShareQRScreen } from '../app/share-qr'
import { NotificationsScreen } from '../app/notifications'
import { NotificationsSettingsScreen } from '../app/notifications-settings'
import SavingsPage from '../app/savings'
import SelectGoalPage from '../app/savings/select'
import SetGoalPage from '../app/savings/set'
import FirstDepositPage from '../app/savings/first-deposit'
import TransferInPage from '../app/savings/transfer-in'
import TransferInConfirmPage from '../app/savings/transfer-in/confirm-transfer'
import TransferInSuccessPage from '../app/savings/transfer-in/success'
import TransferOutPage from '../app/savings/transfer-out'
import TransferOutConfirmPage from '../app/savings/transfer-out/confirm-transfer'
import TransferOutSuccessPage from '../app/savings/transfer-out/success'
import SavingsDetailScreen from '../app/savings/savings-detail/[goalId]/index'
import { SavingsTransactionsListScreen } from '../app/savings/transactions-list/[goalId]'
import SavingsTransferDetailsScreen from '../app/savings/transfer-details/[goalId]/[transferId]'
import { SecurityScreen } from '../app/security'
import SecurityCreateNewPasswordScreen from '../app/security/create-new-password'
import BlockedUsersScreen from '../app/security/blocked-users'
import RequestYourDataScreen from '../app/security/request-your-data'
import { FaqsScreen } from '../app/faqs'
import { ConversionScreen } from '../app/conversion'
import { ConversionSuccessScreen } from '../app/conversion/success'
import { PortfolioScreen } from '../app/portfolio'
import { CurrencyDetailsScreen } from '../app/currency-details'
import { CurrencyActivitiesScreen } from '../app/currency-details/activities'
import { UserProfileScreen } from '../app/user-profile/[userId]'
import { ProfileNonUserScreen } from '../app/profile-non-user'
import { TransactionDetailsScreen } from '../app/transaction-details/[transactionId]'
import { UserTransactionsScreen } from '../app/user-transactions'
import { ReportUserScreen } from '../app/report-user'
import CheckYourIdentityScreen from '../app/check-your-identity'
import ValidationsByFeatureScreen from '../app/validations-by-feature'
import { InviteFriendsScreen } from '../app/invite-friends'
import OnRampWebviewScreen from '../app/on-ramp-webview'
import ServiceUnavailableScreen from '../app/service-unavailable'
import { CashbackInfoScreen } from '../app/cashback-info'

// AI Support screen imports
import AISupportScreen from '../app/ai-support'
import AIChatsListScreen from '../app/ai-support/chats'

// Withdraw screen imports
import { WithdrawScreen } from '../app/withdraw'
import { WithdrawReviewAndConfirmScreen } from '../app/withdraw/review-and-confirm'
import { WithdrawChooseAddressScreen } from '../app/withdraw/choose-address'
import { WithdrawRegisterWalletAddressScreen } from '../app/withdraw/register-wallet-address'
import { WithdrawScanWalletAddressScreen } from '../app/withdraw/scan-wallet-address'
import { WithdrawVerifyCodeScreen } from '../app/withdraw/verify-code'
import { WithdrawSuccessScreen } from '../app/withdraw/success'

// Sell-money screen imports
import { SelectCurrencySellScreen } from '../app/sell-money/select-currency-sell'

// MFA screen import
import { MFACodeValidatorScreen } from '../app/mfa-code-validator'

// Boxo mini-app screen
import { BoxoMiniappScreen } from '../app/boxo-miniapp/[appId]'

// Stocks screens (lazy loaded)
import React, { Suspense } from 'react'
const ConfirmSellMoneyScreen = React.lazy(() => import('../app/sell-money/confirm'))
const StocksScreen = React.lazy(() => import('../app/stocks'))
const StockDetailsScreen = React.lazy(() => import('../app/stocks/details'))
const BuyStocksEnterAmountScreen = React.lazy(() => import('../app/stocks/buy/enter-amount'))
const BuyReviewOrderScreen = React.lazy(() => import('../app/stocks/buy/review-order'))
const SellStocksEnterAmountScreen = React.lazy(() => import('../app/stocks/sell/enter-amount'))
const SellReviewOrderScreen = React.lazy(() => import('../app/stocks/sell/review-order'))
const ProcessingOrderScreen = React.lazy(() => import('../app/stocks/processing-order'))
const StocksKycPendingScreen = React.lazy(() => import('../app/stocks/kyc-pending'))
const SetCardPinScreen = React.lazy(() => import('../app/kem-card/manage-card-pin/set-pin'))

// Off-ramp screens (lazy loaded)
const OffRampSelectCurrency = React.lazy(() => import('../app/withdraw/off-ramp/select-receiving-currency'))
const OffRampEnterAmount = React.lazy(() => import('../app/withdraw/off-ramp/enter-amount'))
const OffRampSelectBank = React.lazy(() => import('../app/withdraw/off-ramp/select-bank-account'))
const OffRampPaymentDetails = React.lazy(() => import('../app/withdraw/off-ramp/payment-details'))
const OffRampReviewConfirm = React.lazy(() => import('../app/withdraw/off-ramp/review-and-confirm'))
const OffRampProcessing = React.lazy(() => import('../app/withdraw/off-ramp/processing'))
const OffRampCompleted = React.lazy(() => import('../app/withdraw/off-ramp/transaction-completed'))
const OffRampFailed = React.lazy(() => import('../app/withdraw/off-ramp/transaction-canceled-or-failed'))
const OffRampQuotesUnavailable = React.lazy(() => import('../app/withdraw/off-ramp/price-quotes-unavailable'))
const OffRampKycPending = React.lazy(() => import('../app/withdraw/off-ramp/kyc-pending'))

// Route protection components
import { ProtectedRoute, AppLayout } from '../components'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: <MainScreen />,
      },
      {
        path: 'auth',
        element: (
          <SafeAreaLayout isStretch hasStickyHeader>
            <AuthLayout />
          </SafeAreaLayout>
        ),
        children: [
          {
            path: 'identification-method',
            element: <IdentificationMethodScreen />,
          },
          {
            path: 'identifier/with-email',
            element: <IdentifyWithEmailScreen />,
          },
          {
            path: 'identifier/with-phone',
            element: <IdentifyWithPhoneScreen />,
          },
          {
            path: 'enter-password',
            element: <EnterPasswordScreen />,
          },
          {
            path: 'signup/create-password',
            element: <SignUpCreatePasswordScreen />,
          },
          {
            path: 'signup/enter-verification-code',
            element: <SignUpEnterVerificationCodeScreen />,
          },
          {
            path: 'forgot-password/recover-with-email',
            element: <RecoverPasswordWithEmailScreen />,
          },
          {
            path: 'forgot-password/recover-with-phone',
            element: <RecoverPasswordWithPhoneScreen />,
          },
          {
            path: 'forgot-password/verification-code',
            element: <ForgotPasswordVerificationCodeScreen />,
          },
          {
            path: 'forgot-password/create-new-password',
            element: <CreateNewPasswordScreen />,
          },
        ],
      },
      {
        path: 'setup-profile',
        element: (
          <SafeAreaLayout isStretch hasStickyHeader>
            <ProtectedRoute>
              <SetupProfileScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <SafeAreaLayout hasNavBar>
            <ProtectedRoute>
              <DashboardScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'savings',
        element: (
          <ProtectedRoute>
            <SavingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/select',
        element: (
          <ProtectedRoute>
            <SelectGoalPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/set',
        element: (
          <ProtectedRoute>
            <SetGoalPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/first-deposit',
        element: (
          <ProtectedRoute>
            <FirstDepositPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-in',
        element: (
          <ProtectedRoute>
            <TransferInPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-in/confirm-transfer',
        element: (
          <ProtectedRoute>
            <TransferInConfirmPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-in/success',
        element: (
          <ProtectedRoute>
            <TransferInSuccessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-out',
        element: (
          <ProtectedRoute>
            <TransferOutPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-out/confirm-transfer',
        element: (
          <ProtectedRoute>
            <TransferOutConfirmPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transfer-out/success',
        element: (
          <ProtectedRoute>
            <TransferOutSuccessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/savings-detail/:goalId',
        element: (
          <ProtectedRoute>
            <SavingsDetailScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'savings/transactions-list/:goalId',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <SavingsTransactionsListScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'savings/transfer-details/:goalId/:transferId',
        element: (
          <ProtectedRoute>
            <SavingsTransferDetailsScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tabs',
        element: <Navigate to="/dashboard" replace />, // Redirect for mobile compatibility
      },
      {
        path: '(tabs)',
        element: <Navigate to="/dashboard" replace />, // Redirect for mobile compatibility
      },
      {
        path: 'debug-api',
        element: (
          <SafeAreaLayout>
            <DebugApiScreen />
          </SafeAreaLayout>
        ),
      },
      {
        path: 'profile/profile-settings',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <ProfileSettingsScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'profile/edit-profile',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <EditProfileScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'profile/update-phone-number',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <UpdatePhoneNumberScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'profile/update-phone-number/verify',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <VerifyPhoneNumberScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'profile/update-profile',
        element: (
          <ProtectedRoute>
            <UpdateProfileScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-email/add-new-email',
        element: (
          <ProtectedRoute>
            <AddNewEmailScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-email/validate-code',
        element: (
          <ProtectedRoute>
            <ValidateEmailCodeScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-email/new-email-validate-code',
        element: (
          <ProtectedRoute>
            <NewEmailValidateCodeScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-phone/add-new-phone-number',
        element: (
          <ProtectedRoute>
            <AddNewPhoneNumberScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-phone/phone-number-validate-code',
        element: (
          <ProtectedRoute>
            <ValidatePhoneCodeScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile/update-profile/update-phone/new-phone-number-validate-code',
        element: (
          <ProtectedRoute>
            <NewPhoneValidateCodeScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'choose-language',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <ChooseLanguageScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'choose-payment-currency',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <ChoosePaymentCurrencyScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'successful-profile-update',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <SuccessfulProfileUpdateScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'activity',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <ActivityScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'request-details/:requestId',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <RequestDetailsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'transaction-details/:transactionId',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <TransactionDetailsScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'requests',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <RequestsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'payment-link/:code',
        element: (
          <ProtectedRoute>
            <PaymentLinkClaimScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request',
        element: (
          <ProtectedRoute>
            <SendRequestScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request/with-user',
        element: (
          <ProtectedRoute>
            <SendRequestWithUserScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request/recipient-selection',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <RecipientSelectionScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'send-request/recipient',
        element: (
          <ProtectedRoute>
            <SendRequestRecipientScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request/confirm',
        element: (
          <ProtectedRoute>
            <SendRequestConfirmScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request/success',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <TransactionSuccessScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'send-request/confirm-multiple',
        element: (
          <ProtectedRoute>
            <MultipleRecipientsConfirmScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'send-request/success-multiple',
        element: (
          <ProtectedRoute>
            <MultipleRequestsSuccessScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'split-request/select-users',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <SplitRequestSelectUsersScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'split-request/confirm',
        element: (
          <ProtectedRoute>
            <SplitRequestConfirmScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'split-request/success',
        element: (
          <ProtectedRoute>
            <SplitRequestSuccessScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'community',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <CommunityScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'invite-friends',
        element: (
          <ProtectedRoute>
            <InviteFriendsScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'kem-card',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <KemCardScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/choose-style',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <KemCardChooseStyleScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/info-confirmation',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <KemCardInfoConfirmationScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/update-legal-name',
        element: (
          <ProtectedRoute>
            <UpdateLegalNameScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'kem-card/details',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <KemCardDetailsScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/card-info',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <KemCardInfoScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/wallet-instructions',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <KemCardWalletInstructionsScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/creation-failure',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <KemCardCreationFailureScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'kem-card/activities',
        element: (
          <ProtectedRoute>
            <KemCardActivitiesScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'kem-card/manage-card-pin/set-pin',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SetCardPinScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'add-money/select-deposit-type',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <SelectDepositTypeScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'add-money/select-currency',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <SelectCurrencyScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'add-money',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <AddMoneyScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'add-money/confirm',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <ConfirmAddMoneyScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'add-money/success',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <AddMoneySuccessScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'apple-pay-checkout',
        element: <ApplePayCheckoutScreen />,
      },
      {
        path: 'add-phone',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <AddPhoneScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'add-phone/enter-phone',
        element: (
          <SafeAreaLayout hasStickyHeader isStretch>
            <ProtectedRoute>
              <EnterPhoneScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'add-phone/verification',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <AddPhoneVerificationScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'check-your-email',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <CheckYourEmailScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'currency-deposit',
        element: (
          <ProtectedRoute>
            <CurrencyDepositScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'share-qr',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <ShareQRScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'notifications',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <NotificationsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'notifications-settings',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <NotificationsSettingsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'security',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <SecurityScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'security/create-new-password',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <SecurityCreateNewPasswordScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'security/blocked-users',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <BlockedUsersScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'security/request-your-data',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <RequestYourDataScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'faqs',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <FaqsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'conversion',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <ConversionScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'conversion/success',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <ConversionSuccessScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'portfolio',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <PortfolioScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'currency-details',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <CurrencyDetailsScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'currency-details/activities',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <CurrencyActivitiesScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'user-profile/:userId',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <UserProfileScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'user-transactions',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <UserTransactionsScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'report-user',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <ReportUserScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'profile-non-user',
        element: (
          <SafeAreaLayout>
            <ProtectedRoute>
              <ProfileNonUserScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'check-your-identity',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <CheckYourIdentityScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'validations-by-feature',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <ValidationsByFeatureScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'on-ramp-webview',
        element: (
          <SafeAreaLayout isStretch hasStickyHeader>
            <ProtectedRoute>
              <OnRampWebviewScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'service-unavailable',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <ServiceUnavailableScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <WithdrawScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/review-and-confirm',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <WithdrawReviewAndConfirmScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/choose-address',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <WithdrawChooseAddressScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/register-wallet-address',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <WithdrawRegisterWalletAddressScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/scan-wallet-address',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <WithdrawScanWalletAddressScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/verify-code',
        element: (
          <SafeAreaLayout hasStickyHeader>
            <ProtectedRoute>
              <WithdrawVerifyCodeScreen />
            </ProtectedRoute>
          </SafeAreaLayout>
        ),
      },
      {
        path: 'withdraw/success',
        element: (
          // <SafeAreaLayout>
          <ProtectedRoute>
            <WithdrawSuccessScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'sell-money/select-currency-sell',
        element: (
          <ProtectedRoute>
            <SelectCurrencySellScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sell-money/confirm',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ConfirmSellMoneyScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'cashback-info',
        element: (
          <ProtectedRoute>
            <CashbackInfoScreen />
          </ProtectedRoute>
        ),
      },
      // {
      //   path: 'ai-support',
      //   element: (
      //     <ProtectedRoute>
      //       <AISupportScreen />
      //     </ProtectedRoute>
      //   ),
      // },
      // {
      //   path: 'ai-support/chats',
      //   element: (
      //     <ProtectedRoute>
      //       <AIChatsListScreen />
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: 'mfa-code-validator',
        element: (
          // <SafeAreaLayout hasStickyHeader>
          <ProtectedRoute>
            <MFACodeValidatorScreen />
          </ProtectedRoute>
          // </SafeAreaLayout>
        ),
      },
      {
        path: 'boxo-miniapp/:appId',
        element: (
          <ProtectedRoute>
            <BoxoMiniappScreen />
          </ProtectedRoute>
        ),
      },
      // Stocks routes
      {
        path: 'stocks',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <StocksScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/details/:symbol',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <StockDetailsScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/buy/enter-amount',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <BuyStocksEnterAmountScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/buy/review-order',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <BuyReviewOrderScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/sell/enter-amount',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SellStocksEnterAmountScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/sell/review-order',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SellReviewOrderScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/kyc-pending',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <StocksKycPendingScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stocks/processing-order',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ProcessingOrderScreen />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Off-ramp routes
      {
        path: 'withdraw/off-ramp/select-receiving-currency',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampSelectCurrency />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/enter-amount',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampEnterAmount />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/select-bank-account',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampSelectBank />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/payment-details',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampPaymentDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/review-and-confirm',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampReviewConfirm />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/processing',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampProcessing />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/transaction-completed',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampCompleted />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/transaction-canceled-or-failed',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampFailed />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/price-quotes-unavailable',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampQuotesUnavailable />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'withdraw/off-ramp/kyc-pending',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffRampKycPending />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
